import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  DollarCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Alert, Button, Checkbox, Divider, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Tag, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import amazonicon from '~/assets/amazonicon.svg';
import bestbuyicon from '~/assets/bestbuyicon.svg';
import canadaposticon from '~/assets/canadaposticon.svg';
import dhlicon from '~/assets/dhlicon.svg';
import ebayicon from '~/assets/ebayicon.svg';
import fedexicon from '~/assets/fedexicon.svg';
import manualicon from '~/assets/manualicon.svg';
import neweggicon from '~/assets/neweggicon.svg';
import purolatoricon from '~/assets/purolatoricon.svg';
import shopifyicon from '~/assets/shopifyicon.svg';
import upsicon from '~/assets/upsicon.svg';
import walmarticon from '~/assets/walmarticon.svg';
import { DebounceSelect } from '~/components/DebounceSelect';
import api from '~/services/api';
import history from '~/services/history';
import CanadianProvinces from '~/utils/canadianProvinces';
import CountryList from '~/utils/countriesList';
import UsStates from '~/utils/usStates';

import { generatePDF } from './generatePDF';
import {
  ActionBar,
  ActionsGroup,
  ComponentTextArea,
  Container,
  CustomDivider,
  FormRow,
  HeaderMeta,
  LinkIcon,
  OrderArea,
  OrderAreaProducts,
  OrderContainer,
  OrderSummary,
  OrderTitle,
  ProductsTable,
  ProductsTableShipped,
  SearchProducts,
  SectionHeader,
  SectionHeaderInline,
  SectionHeading,
  StatusRow,
  StoreIcon,
  StoreIdentity,
  StoreNameText,
  StyledIcon,
  StyledTag,
  TableScroll,
  TrackingLink,
  WarningCallout,
  WarningContent,
  WarningIconWrap,
  WarningText,
} from './styles';

export default function OrderDetail({ match, location }) {
  const [form] = Form.useForm();
  const { Paragraph, Text, Title } = Typography;
  const profile = useSelector((state) => state.user.profile);

  const [saveConfirmPopup, setSaveConfirmPopup] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [error, setError] = useState('');
  const [orderDetails, setOrderDetails] = useState({ customer: {}, store: {}, order_items: [{}], checklist: [{ technician: {}, item: {} }] });
  const [loadingSync, setLoadingSync] = useState(false);
  const [loadingAmazonLabels, setLoadingAmazonLabels] = useState(false);
  const [amazonLabelsModalOpen, setAmazonLabelsModalOpen] = useState(false);
  const [amazonLabelSerials, setAmazonLabelSerials] = useState([]);
  const [selectedAmazonLabelIndexes, setSelectedAmazonLabelIndexes] = useState([]);
  const [lastAmazonLabelClickedIndex, setLastAmazonLabelClickedIndex] = useState(null);
  const [value, setValue] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [packingSlipNotes, setPackingSlipNotes] = useState('');
  const [trackingLink, setTrackingLink] = useState('');
  const [replacementUsers, setReplacementUsers] = useState([]);
  const [replacementUsersAllowedDelete, setReplacementUsersAllowedDelete] = useState([]);
  const [allowedPrintAmazonLabelsUserIds, setAllowedPrintAmazonLabelsUserIds] = useState([]);
  const [replacementOrderNotes, setReplacementOrderNotes] = useState('');
  const [genuineDelayed, setGenuineDelayed] = useState(false);
  const [replacementDrawerOpen, setReplacementDrawerOpen] = useState(false);
  const [creatingReplacementOrder, setCreatingReplacementOrder] = useState(false);
  const [replacementInstructionsDrawerOpen, setReplacementInstructionsDrawerOpen] = useState(false);
  const [replacementReason, setReplacementReason] = useState('');
  const [sourceOrderDetails, setSourceOrderDetails] = useState(null);
  const [loadingSourceOrderDetails, setLoadingSourceOrderDetails] = useState(false);
  const [replacementForm, setReplacementForm] = useState({
    orderNumber: '',
    shipByDate: '',
    orderStatus: 'awaiting_shipment',
    customerUsername: '',
    customerEmail: '',
    shipTo: {
      name: '',
      company: '',
      street1: '',
      street2: '',
      street3: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phone: '',
      residential: false,
      addressVerified: '',
    },
    items: [{ sku: '', name: '', quantity: 1 }],
    internalNotes: '',
  });

  const ADDRESS_FIELDS = [
    'name',
    'company',
    'street1',
    'street2',
    'street3',
    'city',
    'state',
    'postalCode',
    'country',
    'phone',
    'residential',
    'addressVerified',
  ];

  const ADDRESS_LABELS = {
    name: 'Name',
    company: 'Company',
    street1: 'Street 1',
    street2: 'Street 2',
    street3: 'Street 3',
    city: 'City',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country',
    phone: 'Phone',
    residential: 'Residential',
    addressVerified: 'Address Verified',
  };

  const allCountryOptions = useMemo(() => CountryList(), []);
  const prioritizedCountryOptions = useMemo(() => {
    const priorityCodes = ['CA', 'US'];
    const priority = priorityCodes.map((code) => allCountryOptions.find((country) => country.value === code)).filter(Boolean);
    const remainder = allCountryOptions.filter((country) => !priorityCodes.includes(country.value));
    return [...priority, ...remainder];
  }, [allCountryOptions]);

  const storeItemsList = useMemo(
    () => (orderDetails.order_items || []).filter((el) => el.is_rbms_item === false).sort((a, b) => a.id - b.id),
    [orderDetails.order_items]
  );

  const productOrderList = useMemo(() => (orderDetails.order_items || []).filter((el) => el.is_rbms_item === true), [orderDetails.order_items]);

  const parseAllowedUsers = useCallback((rawData) => {
    const rawValue = Array.isArray(rawData) ? rawData[0]?.value_param || '' : rawData?.value_param || rawData || '';
    return String(rawValue)
      .split(';')
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
  }, []);

  const parseAllowedUserIds = useCallback((rawData) => {
    const rawValue = Array.isArray(rawData) ? rawData[0]?.value_param || '' : rawData?.value_param || rawData || '';
    return String(rawValue)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount || 0));

  const formatDate = (date) => (date ? moment(date).format('MMM DD, YYYY') : '--');

  const normalizeCarrierCode = (carrierValue) =>
    String(carrierValue || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

  const carrierGroupFromCode = (code) => {
    const normalized = normalizeCarrierCode(code);

    if (normalized.includes('dhl')) return 'dhl';
    if (normalized.includes('fedex')) return 'fedex';
    if (normalized.includes('ups')) return 'ups';
    if (normalized.includes('usps')) return 'usps';
    if (normalized.includes('purolator')) return 'purolator';
    if (normalized.includes('canada post')) return 'canada_post';
    if (normalized.includes('amazon')) return 'amazon';
    if (normalized.includes('paquete')) return 'paquete_express';
    if (normalized === 'other') return 'other';

    return 'other';
  };

  const getCarrierCompanyName = (carrierCode) => {
    const labels = {
      dhl: 'DHL',
      fedex: 'FedEx',
      ups: 'UPS',
      usps: 'USPS',
      purolator: 'Purolator',
      canada_post: 'Canada Post',
      amazon: 'Amazon Buy Shipping',
      paquete_express: 'Paquete Express',
      other: 'Other',
    };

    return labels[carrierGroupFromCode(carrierCode)] || 'Other';
  };

  useEffect(() => {
    const { search } = location;
    const params = new URLSearchParams(search);

    const searchQueryParam = params.get('searchQuery');
    setSearchQuery(searchQueryParam);

    // const storeIdParam = params.get('storeId');
    // setStoreId(storeIdParam);

    async function loadOrder() {
      const { data: orderData } = await api.get(`orders/${match.params.id}`);
      if (orderData.error) {
        setError(orderData.error);
        toast.warn('No orders found!');
        return;
      }

      if (orderData && orderData.status === 'shipped') {
        let linkToTrackingCode = '';

        switch (carrierGroupFromCode(orderData.carrier_code)) {
          case 'ups':
            linkToTrackingCode = `https://wwwapps.ups.com/WebTracking/processRequest?HTMLVersion=5.0&Requester=NES&AgreeToTermsAndConditions=yes&loc=en_US&tracknum=${orderData.tracking_number}/trackdetails`;
            break;
          case 'purolator':
            linkToTrackingCode = `https://www.purolator.com/en/shipping/tracker?pins=${orderData.tracking_number}`;
            break;
          case 'dhl':
            linkToTrackingCode = `http://www.dhl.com/content/g0/en/express/tracking.shtml?brand=DHL&AWB=${orderData.tracking_number}`;
            break;
          case 'fedex':
            linkToTrackingCode = `https://www.fedex.com/fedextrack/?trknbr=${orderData.tracking_number}`;
            break;
          case 'canada_post':
            linkToTrackingCode = `http://www.canadapost.ca/cpotools/apps/track/personal/findByTrackNumber?trackingNumber=${orderData.tracking_number}&LOCALE=en`;
            break;
          default:
            break;
        }
        setTrackingLink(linkToTrackingCode);
      }

      setOrderDetails(orderData);
      setOrderNotes(orderData.notes);
      setPackingSlipNotes(orderData.packing_slip_notes);
      setReplacementOrderNotes(orderData.replacement_order_notes);
      setOrderNumber(orderData.order_num);

      // Assigning value to the Order items rbms_notes.
      orderData.order_items.forEach((orderItem, index) => {
        form.setFieldsValue({ [`rbms_notes${index}`]: orderItem.rbms_notes });
      });

      const { data: replacementOrderParamsData } = await api.get('params?service=ReplacementOrder&key_param=usersAllowed');
      setReplacementUsers(parseAllowedUsers(replacementOrderParamsData));

      const { data: replacementUsersAllowedDeleteData } = await api.get('params?service=ReplacementOrder&key_param=usersAllowedToDelete');
      setReplacementUsersAllowedDelete(parseAllowedUsers(replacementUsersAllowedDeleteData));

      const { data: allowedPrintAmazonLabelsData } = await api.get('params?service=FBA&key_param=AllowedPrintAmazonLabels');
      setAllowedPrintAmazonLabelsUserIds(parseAllowedUserIds(allowedPrintAmazonLabelsData));
    }
    loadOrder();
  }, [form, match.params.id, parseAllowedUsers, parseAllowedUserIds]); //eslint-disable-line

  const fetchItems = async (search) => {
    const productsData = await api.get(`/items?search=${search}`);
    return productsData.data.map((el) => ({
      value: `${el.part_number && el.part_number.toUpperCase()} - ${el.description && el.description.toUpperCase()}`,
      label: `${el.part_number && el.part_number.toUpperCase()} - ${el.description && el.description.toUpperCase()}`,
    }));
  };

  // Function to save the notes added.
  const handleSaveNotes = async () => {
    try {
      orderDetails.order_items.forEach(async (orderItem, index) => {
        await api.put(`orderItems/${orderItem.id}`, { rbms_notes: form.getFieldValue(`rbms_notes${index}`) });
        toast.success('Manual Notes saved successfully');
      });
    } catch (err) {
      toast.error(`${err}. Something went wront. Contact support to explain what happened`);
    }
  };

  const handleVisibleChange = (visible) => {
    setSaveConfirmPopup(!visible);
  };

  const handleAddProduct = useCallback(async () => {
    if (form.getFieldValue('productQtyRef') === '' || form.getFieldValue('productTitleRef') === '') {
      toast.error('Neither Product or Quantity can be 0');
      return;
    }

    const newProductOrderItem = {
      order_id: orderDetails.id,
      title: form.getFieldValue('productTitleRef'),
      unit_price: 0,
      quantity: form.getFieldValue('productQtyRef'),
      total_price: 0,
      rbms_notes: form.getFieldValue('manualNotes'),
      is_rbms_item: true,
    };
    try {
      const newOrderItem = await api.post('orderItems', newProductOrderItem);

      newProductOrderItem.id = newOrderItem.data.id;

      toast.success('Order Saved');

      form.setFieldsValue({ productTitleRef: null, productQtyRef: null, manualNotes: null });

      const response = await api.get(`orders/${match.params.id}`);
      setOrderDetails(response.data);
    } catch (err) {
      toast.error(`${err.response.data.error}. Something went wront. Contact support to explain what happened`);
    }
  }, [form, match.params.id, orderDetails.id]);

  const handleRemoveProduct = useCallback(
    async (itemOrder) => {
      try {
        await api.delete(`orderItems/${itemOrder.id}`);

        toast.success('Item Deleted');
        const response = await api.get(`orders/${match.params.id}`);
        setOrderDetails(response.data);
      } catch (err) {
        toast.warn(`${err.response.data.error}. Something went wrong. Contact support to explain what happened`);
      }
    },
    [match.params.id]
  );

  const handleSyncOrder = async () => {
    try {
      setLoadingSync(true);
      const syncedOrder = await api.patch('/orders/sync', {
        shipstationOrderId: orderDetails.shipstation_order_id,
        // orderNumber,
        // fulfillmentChannel: orderDetails.fulfillment_channel,
        // orderDate: orderDetails.order_date,
        orderId: orderDetails.id,
      });

      if (syncedOrder.data.error) {
        toast.error(syncedOrder.data.error);
        return;
      }

      const orderData = await api.get(`orders/${orderDetails.id}`);

      setOrderDetails(orderData.data);

      setLoadingSync(false);
      toast.success('Order up to date');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const { TextArea } = Input;

  if (error) {
    return <Container>{error}</Container>;
  }

  function showOrderStatus(orderStatus, _dropShipped) {
    let tag = '';
    switch (orderStatus) {
      case 'shipped':
        tag = (
          <StyledTag icon={<CheckCircleOutlined />} color="success">
            Shipped
          </StyledTag>
        );
        break;
      case 'Saved':
        tag = (
          <StyledTag icon={<CheckCircleOutlined />} color="success">
            Saved
          </StyledTag>
        );
        break;
      case 'awaiting_shipment':
        tag = (
          <StyledTag icon={<ClockCircleOutlined />} color="processing">
            Awaiting Shipment
          </StyledTag>
        );
        break;
      case 'on_hold':
        tag = (
          <StyledTag icon={<ClockCircleOutlined />} color="processing">
            On Hold
          </StyledTag>
        );
        break;
      case 'awaiting_payment':
        tag = (
          <StyledTag icon={<DollarCircleOutlined />} color="warning">
            Awaiting Payment
          </StyledTag>
        );
        break;
      case 'cancelled':
        tag = (
          <StyledTag icon={<CloseCircleOutlined />} color="error">
            Cancelled
          </StyledTag>
        );
        break;
      default:
        tag = (
          <StyledTag icon={<ExclamationCircleOutlined />} color="error">
            ORDER WITHOUT STATUS
          </StyledTag>
        );
        break;
    }

    // if (dropShipped) {
    //   return (
    //     <>
    //       {tag}
    //       <StyledTag icon={<CheckCircleOutlined />} color="geekblue">
    //         {dropShipped}
    //       </StyledTag>
    //     </>
    //   );
    // }

    return tag;
  }

  const handleOrderNotes = async (notes) => {
    await api.put(`orders/${orderDetails.id}`, { notes });
  };

  const handlePackingSlipNotes = async (packing_slip_notes) => {
    setOrderDetails({ ...orderDetails, packing_slip_notes });
    await api.put(`orders/${orderDetails.id}`, { packing_slip_notes });
  };

  const handleReplacementOrderNotes = async (replacement_order_notes) => {
    setOrderDetails({ ...orderDetails, replacement_order_notes });
    await api.put(`orders/${orderDetails.id}`, { replacement_order_notes });
  };

  const handleGenuineDelayed = async () => {
    setGenuineDelayed(!genuineDelayed);
    setOrderDetails({ ...orderDetails, genuine_delayed: !genuineDelayed });
    await api.put(`orders/${orderDetails.id}`, { genuine_delayed: !genuineDelayed });
  };

  async function handlePrintPackingSlip() {
    generatePDF(orderDetails);
  }

  const openAmazonLabelsModal = () => {
    const serials = (orderDetails.checklist || [])
      .filter((entry) => Boolean(entry?.serial_number))
      .map((entry) => ({
        id: entry?.id || entry?.inventory_items_id || entry?.serial_number,
        serial: entry.serial_number,
        partNumber: entry?.item?.part_number || '--',
        itemName: entry?.item?.name || '--',
      }));

    if (serials.length === 0) {
      toast.error('No serial numbers found in checklist.');
      return;
    }

    setAmazonLabelSerials(serials);
    setSelectedAmazonLabelIndexes(serials.map((_serial, index) => index));
    setLastAmazonLabelClickedIndex(null);
    setAmazonLabelsModalOpen(true);
  };

  const handleAmazonLabelSelectionChange = (index, checked, isShiftPressed) => {
    setSelectedAmazonLabelIndexes((previousIndexes) => {
      const nextIndexes = new Set(previousIndexes);

      if (isShiftPressed && lastAmazonLabelClickedIndex !== null) {
        const start = Math.min(lastAmazonLabelClickedIndex, index);
        const end = Math.max(lastAmazonLabelClickedIndex, index);

        for (let rangeIndex = start; rangeIndex <= end; rangeIndex += 1) {
          if (checked) {
            nextIndexes.add(rangeIndex);
          } else {
            nextIndexes.delete(rangeIndex);
          }
        }
      } else if (checked) {
        nextIndexes.add(index);
      } else {
        nextIndexes.delete(index);
      }

      return Array.from(nextIndexes).sort((a, b) => a - b);
    });

    setLastAmazonLabelClickedIndex(index);
  };

  const handlePrintAmazonLabels = async () => {
    const serials = selectedAmazonLabelIndexes.map((index) => amazonLabelSerials[index]?.serial).filter(Boolean);

    if (serials.length === 0) {
      toast.error('Select at least one serial number.');
      return;
    }

    try {
      setLoadingAmazonLabels(true);
      await api.post('http://127.0.0.1:17891/amazon-inventory-print-serials', serials);
      toast.success('Amazon labels sent to printer.');
      setAmazonLabelsModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not print Amazon labels.');
    } finally {
      setLoadingAmazonLabels(false);
    }
  };

  const mapCustomerToAddress = (customerData = {}) => {
    const rawCountry = customerData.country || '';
    const normalizedCountry =
      typeof rawCountry === 'string' && rawCountry.length === 2
        ? rawCountry.toUpperCase()
        : allCountryOptions.find((country) => country.label.toLowerCase() === String(rawCountry).toLowerCase())?.value || '';

    return {
      name: customerData.name || '',
      company: customerData.company || '',
      street1: customerData.street1 || '',
      street2: customerData.street2 || '',
      street3: customerData.street3 || '',
      city: customerData.city || '',
      state: customerData.state || '',
      postalCode: customerData.postalCode || customerData.postal_code || '',
      country: normalizedCountry,
      phone: customerData.phone || '',
      residential: Boolean(customerData.residential),
      addressVerified: customerData.addressVerified || '',
    };
  };

  const handleReplacementFieldChange = (fieldName, fieldValue) => {
    setReplacementForm((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
  };

  const handleReplacementAddressChange = (section, fieldName, fieldValue) => {
    setReplacementForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [fieldName]: fieldValue,
        ...(fieldName === 'country' ? { state: '' } : {}),
      },
    }));
  };

  const handleReplacementItemChange = (index, fieldName, fieldValue) => {
    setReplacementForm((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = {
        ...nextItems[index],
        [fieldName]: fieldValue,
      };
      return {
        ...prev,
        items: nextItems,
      };
    });
  };

  const handleAddReplacementItem = () => {
    setReplacementForm((prev) => ({
      ...prev,
      items: [...prev.items, { sku: '', name: '', quantity: 1 }],
    }));
  };

  const handleRemoveReplacementItem = (index) => {
    setReplacementForm((prev) => {
      if (prev.items.length === 1) {
        return prev;
      }
      return {
        ...prev,
        items: prev.items.filter((_, itemIndex) => itemIndex !== index),
      };
    });
  };

  const getStateOptions = (countryCode) => {
    if (countryCode === 'US') return UsStates();
    if (countryCode === 'CA') return CanadianProvinces();
    return [];
  };

  const renderAddressField = (section, fieldKey) => {
    const sectionData = replacementForm[section];
    const countryCode = sectionData.country || '';
    const { Option } = Select;

    if (fieldKey === 'residential') {
      return <Input id={`${section}-${fieldKey}`} value={sectionData.residential ? 'Yes' : 'No'} disabled />;
    }

    if (fieldKey === 'addressVerified') {
      return <Input id={`${section}-${fieldKey}`} value={sectionData[fieldKey]} disabled />;
    }

    if (fieldKey === 'country') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <span style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{ADDRESS_LABELS.country || 'Country'}</span>
          <Select
            value={sectionData.country}
            showSearch
            optionFilterProp="children"
            onChange={(countryValue) => handleReplacementAddressChange(section, 'country', countryValue)}
            style={{ width: '100%' }}
          >
            {prioritizedCountryOptions.map((country) => (
              <Option key={`${section}-country-${country.value}`} value={country.value}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <img
                    src={`https://flagcdn.com/w20/${country.value.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${country.value.toLowerCase()}.png 2x`}
                    width="20"
                    alt={country.label}
                  />
                  <span>{country.label}</span>
                </span>
              </Option>
            ))}
          </Select>
        </div>
      );
    }

    if (fieldKey === 'state') {
      const stateOptions = getStateOptions(countryCode);
      if (stateOptions.length > 0) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <span style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{ADDRESS_LABELS.state || 'State'}</span>
            <Select
              value={sectionData.state}
              showSearch
              optionFilterProp="children"
              onChange={(stateValue) => handleReplacementAddressChange(section, 'state', stateValue)}
              style={{ width: '100%' }}
            >
              {stateOptions.map((stateOption) => (
                <Option key={`${section}-state-${stateOption.value}`} value={stateOption.value}>
                  {stateOption.label}
                </Option>
              ))}
            </Select>
          </div>
        );
      }
    }

    return (
      <Input id={`${section}-${fieldKey}`} value={sectionData[fieldKey]} onChange={(e) => handleReplacementAddressChange(section, fieldKey, e.target.value)} />
    );
  };

  const openReplacementOrderDrawer = () => {
    const customerData = orderDetails.customer || {};
    const mappedAddress = mapCustomerToAddress(customerData);

    setReplacementForm({
      orderNumber: (() => {
        const baseOrder = orderDetails.order_num || `Unknown-${orderDetails.id}`;
        const dateStr = moment().format('YYYYMMDD');
        return `RO-${baseOrder}-${dateStr}`;
      })(),
      shipByDate: moment().add(24, 'hours').format('YYYY-MM-DDTHH:mm'),
      orderStatus: 'awaiting_shipment',
      customerUsername: customerData.customer_username || '',
      customerEmail: customerData.email || '',
      shipTo: mappedAddress,
      items: [{ sku: '', name: '', quantity: 1 }],
      internalNotes: '',
    });
    setReplacementDrawerOpen(true);
  };

  const openReplacementInstructionsDrawer = async () => {
    setReplacementInstructionsDrawerOpen(true);

    if (!orderDetails.original_order?.source_order_id) {
      setSourceOrderDetails(null);
      return;
    }

    if (sourceOrderDetails?.id === orderDetails.original_order?.source_order_id) {
      return;
    }

    setLoadingSourceOrderDetails(true);
    try {
      const { data } = await api.get(`orders/${orderDetails.original_order?.source_order_id}`);
      setSourceOrderDetails(data || null);
    } catch (err) {
      setSourceOrderDetails(null);
      toast.error(`Could not load source order details. ${err.response?.data?.error || err.message || err}`);
    } finally {
      setLoadingSourceOrderDetails(false);
    }
  };

  const getStoreIcon = (storeId) => {
    const icons = {
      1: amazonicon,
      2: amazonicon,
      3: amazonicon,
      12: bestbuyicon,
      10: ebayicon,
      6: neweggicon,
      7: neweggicon,
      8: neweggicon,
      5: manualicon,
      16: shopifyicon,
      14: walmarticon,
      15: walmarticon,
    };
    return icons[storeId] || null;
  };

  const escapeHtml = (val = '') =>
    String(val).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const buildReplacementInstructionsHtml = () => {
    const sourceStore = sourceOrderDetails?.store || {};
    const sourceLogo = getStoreIcon(sourceStore?.id);
    const customer = sourceOrderDetails?.customer || orderDetails?.customer || {};
    const replacementItems = orderDetails?.order_items || [];
    const sourceItems = sourceOrderDetails?.order_items || [];
    const addressParts = [
      customer.company,
      customer.street1,
      customer.street2,
      customer.street3,
      customer.city,
      customer.state,
      customer.postalCode || customer.postal_code,
      customer.country,
    ].filter(Boolean);

    const replacementOrderNumberLabel = unescape(orderDetails?.order_num || '--');
    const sourceOrderNumberLabel = unescape(sourceOrderDetails?.order_num || '--');
    const replacementTitle = `REPLACEMENT ORDER ${replacementOrderNumberLabel}`;
    const purchaseDate = sourceOrderDetails?.order_date ? moment(sourceOrderDetails.order_date).format('MMMM DD, YYYY') : '--';
    const shipDate = sourceOrderDetails?.ship_date ? moment(sourceOrderDetails.ship_date).format('MMMM DD, YYYY') : '--';

    const sourceItemRows = sourceItems
      .map(
        (item) => `
          <tr>
            <td>${escapeHtml(item.seller_sku || item.sku || '--')}</td>
            <td>${escapeHtml(item.title || item.name || '--')}</td>
            <td>${escapeHtml(item.quantity || '--')}</td>
          </tr>
        `
      )
      .join('');

    const replacementItemRows = replacementItems
      .map(
        (item) => `
          <tr>
            <td>${escapeHtml(item.seller_sku || item.sku || '--')}</td>
            <td>${escapeHtml(item.title || item.name || '--')}</td>
            <td>${escapeHtml(item.quantity || '--')}</td>
          </tr>
        `
      )
      .join('');

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(replacementTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
            .header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #e5e7eb; }
            .title { font-size: 22px; font-weight: 700; margin: 0; letter-spacing: 0.2px; }
            .store { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: #1f2937; }
            .store img { width: 26px; height: 26px; object-fit: contain; }
            .section { margin-top: 14px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
            .section h3 { margin: 0; padding: 10px 12px; font-size: 14px; letter-spacing: 0.2px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
            .section-body { padding: 12px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
            .label { font-weight: 600; color: #374151; margin-right: 6px; }
            .subheading { margin: 14px 0 8px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.6px; color: #4b5563; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th, td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; font-size: 12px; }
            th { background: #f3f4f6; font-weight: 600; }
            .reason-box { border: 1px solid #d1d5db; border-radius: 6px; padding: 10px; min-height: 90px; white-space: pre-wrap; background: #fff; }
            .muted { color: #6b7280; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">${escapeHtml(replacementTitle)}</h1>
            <div class="store">
              ${sourceLogo ? `<img src="${sourceLogo}" alt="Store Logo" />` : ''}
              <span>${escapeHtml(sourceStore?.name || sourceOrderDetails?.store?.name || orderDetails?.store?.name || 'Store')}</span>
            </div>
          </div>

          <div class="section">
            <h3>Order Information</h3>
            <div class="section-body">
              <div class="grid">
                <div><span class="label">Order Number:</span>${escapeHtml(sourceOrderNumberLabel)}</div>
                <div><span class="label">Purchase Date:</span>${escapeHtml(purchaseDate)}</div>
                <div><span class="label">Ship Date:</span>${escapeHtml(shipDate)}</div>
              </div>

              <div class="subheading">Source Order Items</div>
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${sourceItemRows || '<tr><td colspan="3" class="muted">No items found on source order.</td></tr>'}
                </tbody>
              </table>
            </div>
          </div>

          <div class="section">
            <h3>Customer Information</h3>
            <div class="section-body">
              <div><span class="label">Name:</span>${escapeHtml(customer.name || '--')}</div>
              <div><span class="label">Address:</span>${escapeHtml(addressParts.join(', ') || '--')}</div>
              <div><span class="label">Phone:</span>${escapeHtml(customer.phone || '--')}</div>
            </div>
          </div>

          <div class="section">
            <h3>Replacement Order Items</h3>
            <div class="section-body">
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${replacementItemRows || '<tr><td colspan="3" class="muted">No items found on replacement order.</td></tr>'}
                </tbody>
              </table>
            </div>
          </div>

          <div class="section">
            <h3>Reason for Replacement</h3>
            <div class="section-body">
              <div class="reason-box">${escapeHtml(replacementReason.trim())}</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrintReplacementInstructions = () => {
    if (!replacementReason.trim()) {
      toast.error('Please type the reason of the replacement before printing.');
      return;
    }

    const html = buildReplacementInstructionsHtml();
    const printWindow = window.open('', '_blank', 'width=960,height=760');

    if (!printWindow) {
      toast.error('Unable to open print preview. Please allow pop-ups for this site.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  const handleCreateReplacementOrder = async () => {
    const validItems = (replacementForm.items || []).filter((item) => item.name && Number(item.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one item with name and quantity.');
      return;
    }

    setCreatingReplacementOrder(true);
    try {
      const payload = {
        orderNumber: replacementForm.orderNumber,
        shipByDate: replacementForm.shipByDate,
        orderStatus: 'awaiting_shipment',
        customerUsername: replacementForm.customerUsername || null,
        customerEmail: replacementForm.customerEmail || null,
        shipTo: replacementForm.shipTo,
        items: validItems.map((item) => ({
          sku: item.sku,
          name: item.name,
          quantity: Number(item.quantity),
        })),
        internalNotes: replacementForm.internalNotes,
        sourceOrderId: orderDetails.id,
      };

      let response;
      try {
        response = await api.post('orders/replacement', payload);
      } catch (replacementEndpointError) {
        response = await api.post('orders', payload);
      }

      const responseData = response.data || {};
      const newOrderId = responseData.order_id || responseData.id;

      setReplacementDrawerOpen(false);
      toast.success(`Replacement order created successfully.`);

      if (newOrderId) {
        history.push(`/order/${newOrderId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create replacement order.');
    } finally {
      setCreatingReplacementOrder(false);
    }
  };

  const getCarrierIcon = (carrierCode) => {
    const group = carrierGroupFromCode(carrierCode);
    const icons = {
      ups: upsicon,
      fedex: fedexicon,
      purolator: purolatoricon,
      dhl: dhlicon,
      canada_post: canadaposticon,
    };
    return icons[group] || null;
  };

  const allowPrintLabel = profile.type === 999 || profile.email === 'hassan.najim@ruggedbooks.com' || profile.email === 'a.haj@ruggedbooks.com';
  const isReplacementOrderDelayed = orderNumber.substring(0, 3).toUpperCase() === 'RO-' && orderDetails.lateReplacementeOrderTimeTotalHours > 24;
  const normalizedProfileEmail = String(profile?.email || '').toLowerCase();
  const normalizedProfileId = String(profile?.id || '');
  const canEditReplacementNotes = replacementUsers.includes(normalizedProfileEmail);
  const canCreateReplacementOrder = orderDetails.status === 'shipped' && replacementUsersAllowedDelete.includes(normalizedProfileEmail);
  const canPrintAmazonLabels = allowedPrintAmazonLabelsUserIds.includes(normalizedProfileId);
  const availableAmazonSerialsCount = (orderDetails.checklist || []).map((entry) => entry?.serial_number).filter(Boolean).length;

  return (
    <Container>
      <ActionBar>
        <Button
          variant="contained"
          type="primary"
          icon={<ArrowLeftOutlined />}
          onClick={() => (searchQuery === null ? history.push('/orders') : history.push(`/orders?searchQuery=${searchQuery}`))}
        >
          Back
        </Button>

        <ActionsGroup>
          {orderDetails.fulfillment_channel !== 'AFN' && (
            <>
              <Button loading={loadingSync} variant="contained" type="primary" icon={<CloudDownloadOutlined />} onClick={handleSyncOrder}>
                Sync
              </Button>
              {canCreateReplacementOrder && (
                <Button variant="contained" type="primary" icon={<PlusOutlined />} onClick={openReplacementOrderDrawer}>
                  Create Replacement Order
                </Button>
              )}
              {allowPrintLabel && (
                <Button variant="contained" type="primary" icon={<PrinterOutlined />} onClick={() => history.push(`/orderShipment/${orderDetails.id}`)}>
                  Print Label
                </Button>
              )}
              {canPrintAmazonLabels && (
                <Button
                  loading={loadingAmazonLabels}
                  variant="contained"
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={openAmazonLabelsModal}
                  disabled={availableAmazonSerialsCount === 0}
                >
                  Print Amazon Labels
                </Button>
              )}
            </>
          )}

          {orderDetails.is_replacement_order === true && (
            <Button variant="contained" type="primary" icon={<PrinterOutlined />} onClick={openReplacementInstructionsDrawer}>
              Print replacement instructions
            </Button>
          )}

          <Button variant="contained" type="primary" icon={<PrinterOutlined />} onClick={handlePrintPackingSlip}>
            Print Packing Slip
          </Button>
        </ActionsGroup>
      </ActionBar>

      <Form form={form} name="orderNotesForm">
        <OrderContainer>
          <OrderTitle>
            <SectionHeader>
              <SectionHeading>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Order Details
                </Title>
                {/* <Text type="secondary">Order detail</Text> */}
                <HeaderMeta>
                  <div>
                    <Paragraph copyable style={{ marginBottom: 8, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                      {unescape(orderNumber)}
                    </Paragraph>
                    <StoreIdentity>
                      {getStoreIcon(orderDetails?.store?.id) && <StoreIcon src={getStoreIcon(orderDetails.store.id)} alt={`${orderDetails.store.name} icon`} />}
                      <StoreNameText>{orderDetails?.store?.name || 'Store unavailable'}</StoreNameText>
                    </StoreIdentity>
                  </div>
                </HeaderMeta>
              </SectionHeading>
            </SectionHeader>

            <StatusRow>
              {showOrderStatus(orderDetails.status, orderDetails?.externally_fulfilled_by)}
              {orderDetails.status && orderDetails.tracking_number && trackingLink && (
                <TrackingLink target="_blank" rel="noopener noreferrer" href={trackingLink}>
                  {orderDetails.carrier_code && <StyledIcon src={getCarrierIcon(orderDetails.carrier_code)} alt={`${orderDetails.carrier_code} icon`} />}
                  {orderDetails.latestTrackingUpdate || 'Track shipment'}
                  <LinkIcon>
                    <FaExternalLinkAlt />
                  </LinkIcon>
                </TrackingLink>
              )}
              {orderDetails.fulfillment_channel === 'AFN' && <Tag color="cyan">FBA</Tag>}
            </StatusRow>

            <CustomDivider />

            <OrderSummary>
              <div>
                <span>Order Number</span>
                <span>{unescape(orderNumber) || '--'}</span>
              </div>
              <div>
                <span>Store</span>
                <span>{orderDetails?.store?.name || '--'}</span>
              </div>
              <div>
                <span>Tracking Number</span>
                <span>{orderDetails.tracking_number || '--'}</span>
              </div>
              <div>
                <span>Carrier</span>
                <span>{getCarrierCompanyName(orderDetails.carrier_code)}</span>
              </div>
              <div>
                <span>Fulfillment</span>
                <span>{orderDetails.fulfillment_channel || '--'}</span>
              </div>
            </OrderSummary>
          </OrderTitle>

          <OrderArea>
            <SectionHeader>
              <SectionHeading>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Order Summary
                </Title>
                {/* <Text type="secondary">Core dates and financial totals for this order.</Text> */}
              </SectionHeading>
            </SectionHeader>

            <OrderSummary>
              <div>
                <span>RBMS Order Date</span>
                <span>{formatDate(orderDetails.createdAt)}</span>
              </div>
              <div>
                <span>Marketplace Order Date</span>
                <span>{formatDate(orderDetails.order_date)}</span>
              </div>
              <div>
                <span>Shipping Date</span>
                <span>{orderDetails.status === 'shipped' ? formatDate(orderDetails.ship_date) : '--'}</span>
              </div>
            </OrderSummary>

            <CustomDivider />

            <OrderSummary>
              <div>
                <span>Tax Amount</span>
                <span>{formatCurrency(orderDetails.tax_amount)}</span>
              </div>
              <div>
                <span>Shipping Amount</span>
                <span>{formatCurrency(orderDetails.shipping_amount)}</span>
              </div>
              <div>
                <span>Order Total</span>
                <span>{formatCurrency(orderDetails.order_total)}</span>
              </div>
            </OrderSummary>
          </OrderArea>

          <OrderArea>
            <SectionHeader>
              <SectionHeading>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Ship To
                </Title>
                {/* <Text type="secondary">Recipient and service information from the marketplace order.</Text> */}
              </SectionHeading>
            </SectionHeader>

            <OrderSummary>
              <div>
                <span>Customer</span>
                <span>{orderDetails?.customer?.name || '--'}</span>
              </div>
              <div>
                <span>Username</span>
                <span>{orderDetails?.customer?.customer_username || '--'}</span>
              </div>
            </OrderSummary>

            <CustomDivider />

            <OrderSummary>
              <div>
                <span>Shipping Service</span>
                <span>{orderDetails.requested_shipping_service || '--'}</span>
              </div>
            </OrderSummary>
          </OrderArea>

          <OrderArea>
            <SectionHeader>
              <SectionHeading>
                <Title level={4} style={{ marginBottom: 0 }}>
                  Notes
                </Title>
                {/* <Text type="secondary">Internal notes and packing slip instructions.</Text> */}
              </SectionHeading>
            </SectionHeader>

            <Space direction="vertical" size={18} style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Order Notes
                </Text>
                <ComponentTextArea
                  value={orderNotes || ''}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  onBlur={(e) => handleOrderNotes(e.target.value)}
                  placeholder="Add aditional information about this order."
                  rows={3}
                />
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Packing Slip Notes
                </Text>
                <ComponentTextArea
                  value={packingSlipNotes || ''}
                  onChange={(e) => setPackingSlipNotes(e.target.value)}
                  onBlur={(e) => handlePackingSlipNotes(e.target.value)}
                  placeholder="Notes to be added to packing slip."
                  rows={3}
                />
              </div>
            </Space>
          </OrderArea>

          {isReplacementOrderDelayed && (
            <OrderArea>
              <SectionHeader>
                <SectionHeading>
                  <Title level={4} style={{ marginBottom: 0 }}>
                    Replacement Review
                  </Title>
                  <Text type="secondary">Escalation details for delayed replacement orders.</Text>
                </SectionHeading>
              </SectionHeader>

              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Replacement Order Notes
                  </Text>
                  <ComponentTextArea
                    disabled={!canEditReplacementNotes}
                    value={replacementOrderNotes || ''}
                    onChange={(e) => setReplacementOrderNotes(e.target.value)}
                    onBlur={(e) => handleReplacementOrderNotes(e.target.value)}
                    placeholder="Add aditional information about replacement order."
                    rows={3}
                  />
                </div>

                <Checkbox checked={orderDetails.genuine_delayed} disabled={!canEditReplacementNotes} onChange={handleGenuineDelayed}>
                  Genuine delayed RMA
                </Checkbox>

                <Alert
                  description={`This order took ${orderDetails.lateReplacementeOrderTimeFormatted} to process, which exceeds our 24-hour target.`}
                  type="warning"
                  showIcon
                />
              </Space>
            </OrderArea>
          )}
        </OrderContainer>

        <OrderAreaProducts>
          <SectionHeader>
            <SectionHeading>
              <Title level={4} style={{ marginBottom: 0 }}>
                Products Ordered
              </Title>
              {/* <Text type="secondary">Marketplace line items and editable RBMS notes.</Text> */}
            </SectionHeading>

            <Form.Item style={{ marginBottom: 0 }}>
              <Popconfirm
                placement="bottom"
                open={saveConfirmPopup}
                title="Do you want to save these notes?"
                onConfirm={() => {
                  handleSaveNotes();
                }}
                onOpenChange={() => handleVisibleChange(saveConfirmPopup)}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  disabled={orderDetails.status === 'cancelled' && profile.type !== 999}
                  type="primary"
                  htmlType="button"
                  variant="contained"
                  icon={<SaveOutlined />}
                >
                  Save Notes
                </Button>
              </Popconfirm>
            </Form.Item>
          </SectionHeader>

          {orderDetails.order_items && (
            <TableScroll>
              <ProductsTable>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Unit $</th>
                    <th>SKU</th>
                    <th>Qty</th>
                    <th>Total $ (Before tax)</th>
                    <th>Total Tax $</th>
                    <th>Total $</th>
                    <th>Store Notes</th>
                    <th>Manual Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {storeItemsList.map((itemOrder, index) => (
                    <tr key={itemOrder.id || `store-item-${itemOrder.seller_sku}-${index}`}>
                      <td>{itemOrder.title}</td>
                      <td>{formatCurrency(itemOrder.unit_price)}</td>
                      <td>{itemOrder.seller_sku}</td>
                      <td>{itemOrder.quantity}</td>
                      <td>{formatCurrency(itemOrder.total_price)}</td>
                      <td>{formatCurrency(itemOrder.item_tax)}</td>
                      <td>{formatCurrency(+itemOrder.total_price + +itemOrder.item_tax)}</td>
                      <td>
                        <Tooltip title={itemOrder.notes} color="blue">
                          {itemOrder.notes && `${itemOrder.notes.substring(0, 100)}...`}
                        </Tooltip>
                      </td>
                      <td>
                        {profile?.type === 1 ? (
                          <Form.Item hasFeedback name={`rbms_notes${index}`} style={{ marginBottom: 0 }}>
                            <TextArea
                              showCount
                              maxLength={2000}
                              allowClear
                              style={{ minWidth: 250 }}
                              placeholder="Add additional information about this order"
                              rows={4}
                              readOnly
                            />
                          </Form.Item>
                        ) : (
                          <Form.Item hasFeedback name={`rbms_notes${index}`} style={{ marginBottom: 0 }}>
                            <TextArea
                              showCount
                              maxLength={2000}
                              allowClear
                              style={{ minWidth: 250 }}
                              placeholder="Add additional information about this order"
                              rows={4}
                            />
                          </Form.Item>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ProductsTable>
            </TableScroll>
          )}
        </OrderAreaProducts>
      </Form>

      <SearchProducts>
        <Form form={form} name="addItemsForm">
          <SectionHeader>
            <SectionHeading>
              <Title level={4} style={{ marginBottom: 0 }}>
                Add Items to Order
              </Title>
              <Text type="secondary">When shipping supplementary items not included in the initial purchase</Text>
            </SectionHeading>
          </SectionHeader>

          <FormRow>
            <Form.Item name="productTitleRef">
              <DebounceSelect
                allowClear
                showSearch
                value={value}
                placeholder="Products"
                fetchOptions={(val) => fetchItems(val, false, null, null, false)}
                style={{ width: '100%', textTransform: 'uppercase' }}
                onChange={(newValue) => {
                  setValue(newValue);
                }}
              />
            </Form.Item>

            <Form.Item name="productQtyRef">
              <InputNumber min={0} max={99999} placeholder="Qty" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item hasFeedback name="manualNotes">
              <TextArea showCount maxLength={2000} allowClear style={{ width: '100%' }} placeholder="Add additional information about this order" rows={2} />
            </Form.Item>

            <Button
              disabled={orderDetails.status === 'cancelled' && profile.type !== 999}
              type="primary"
              shape="circle"
              onClick={handleAddProduct}
              icon={<PlusOutlined />}
              style={{ alignSelf: 'center' }}
            />
          </FormRow>

          {productOrderList.length > 0 && (
            <OrderAreaProducts>
              {/* <SectionHeader>
                <SectionHeading>
                  <Title level={4} style={{ marginBottom: 0 }}>
                    Items Added Manually
                  </Title>
                  <Text type="secondary">RBMS-only items currently attached to this order.</Text>
                </SectionHeading>
              </SectionHeader> */}

              <TableScroll>
                <ProductsTable>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Qty</th>
                      <th>Manual Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productOrderList.map((itemOrder, index) => (
                      <tr key={itemOrder.id || index}>
                        <td>{itemOrder.title}</td>
                        <td>{itemOrder.quantity}</td>
                        <td>{itemOrder.rbms_notes}</td>
                        <td>
                          {orderDetails.status === 'awaiting_shipment' ||
                          orderDetails.status === 'on_hold' ||
                          orderDetails.status === 'cancelled' ||
                          profile?.type === 999 ? (
                            <Popconfirm
                              onConfirm={() => handleRemoveProduct(itemOrder, index)}
                              title="Are you sure you want to delete?"
                              trigger="click"
                              placement="left"
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button type="primary" icon={<DeleteOutlined />} />
                            </Popconfirm>
                          ) : (
                            <Button type="primary" icon={<DeleteOutlined />} disabled />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ProductsTable>
              </TableScroll>
            </OrderAreaProducts>
          )}
        </Form>
      </SearchProducts>

      {orderDetails.fulfillment_channel !== 'AFN' && (
        <OrderAreaProducts>
          <SectionHeaderInline>
            <SectionHeading>
              <Title level={4} style={{ marginBottom: 0 }}>
                Products Shipped
              </Title>
              {/* <Text type="secondary">Serial-numbered items already attached to this shipment.</Text> */}
            </SectionHeading>

            {orderDetails.checklist.length >= orderDetails.totalAmountOfItems ? (
              <WarningCallout>
                <WarningIconWrap>
                  <ExclamationCircleOutlined />
                </WarningIconWrap>
                <WarningContent>
                  <WarningText>Scan limit reached. Add more products to continue adding serial-numbered components.</WarningText>
                </WarningContent>
              </WarningCallout>
            ) : (
              <div />
            )}

            <Link to={`/ordersEdit?order_id=${orderDetails.id}`}>
              <Button
                disabled={orderDetails.status === 'cancelled' && profile.type !== 999}
                variant="contained"
                type="primary"
                icon={<PlusOutlined />}
                danger={orderDetails.checklist.length >= orderDetails.totalAmountOfItems}
              >
                Add serial number
              </Button>
            </Link>
          </SectionHeaderInline>

          {orderDetails.checklist && (
            <TableScroll>
              <ProductsTableShipped>
                <thead>
                  <tr>
                    <th>Index</th>
                    <th>Serial Number</th>
                    <th>Part Number</th>
                    <th>Item</th>
                    <th>Technician</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.checklist.map((itemShipped, index) => (
                    <tr key={itemShipped.id || index}>
                      <td>{index + 1}</td>
                      <td>{itemShipped.serial_number}</td>
                      <td>{itemShipped.item?.part_number}</td>
                      <td>{itemShipped.item?.name}</td>
                      <td>{itemShipped.technician.name}</td>
                    </tr>
                  ))}
                </tbody>
              </ProductsTableShipped>
            </TableScroll>
          )}
        </OrderAreaProducts>
      )}

      <Modal
        title="Print Amazon Labels"
        open={amazonLabelsModalOpen}
        onCancel={() => setAmazonLabelsModalOpen(false)}
        onOk={handlePrintAmazonLabels}
        okText="Print Selected"
        cancelText="Cancel"
        confirmLoading={loadingAmazonLabels}
        okButtonProps={{ disabled: selectedAmazonLabelIndexes.length === 0 }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%', maxHeight: 360, overflowY: 'auto' }}>
          {amazonLabelSerials.map((serialEntry, index) => (
            <Checkbox
              key={String(serialEntry.id)}
              checked={selectedAmazonLabelIndexes.includes(index)}
              onChange={(event) => handleAmazonLabelSelectionChange(index, event.target.checked, Boolean(event.nativeEvent?.shiftKey))}
            >
              <div>
                <div>{serialEntry.serial}</div>
                <Text type="secondary">
                  PN: {serialEntry.partNumber} | Item: {serialEntry.itemName}
                </Text>
              </div>
            </Checkbox>
          ))}
        </Space>
      </Modal>

      <Drawer
        title="Print Replacement Instructions"
        placement="right"
        width="48vw"
        open={replacementInstructionsDrawerOpen}
        onClose={() => setReplacementInstructionsDrawerOpen(false)}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Replacement Order
          </Text>
          <Text>{unescape(orderDetails.order_num || '--')}</Text>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Source Order
          </Text>
          <Text>{sourceOrderDetails?.order_num || orderDetails.original_order?.source_order_id || '--'}</Text>
          {loadingSourceOrderDetails && (
            <div>
              <Text type="secondary">Loading source order details...</Text>
            </div>
          )}
        </div>

        <Divider />

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Reason of replacement
          </Text>
          <Input.TextArea
            value={replacementReason}
            onChange={(e) => setReplacementReason(e.target.value)}
            placeholder="Type the replacement reason"
            autoSize={{ minRows: 5, maxRows: 10 }}
          />
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 12, display: 'flex', gap: 8 }}>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrintReplacementInstructions}>
            Print
          </Button>
          <Button onClick={() => setReplacementInstructionsDrawerOpen(false)}>Close</Button>
        </div>
      </Drawer>

      <Drawer title="Create Replacement Order" placement="right" width="60vw" open={replacementDrawerOpen} onClose={() => setReplacementDrawerOpen(false)}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Order Number</div>
            <Input value={replacementForm.orderNumber} onChange={(e) => handleReplacementFieldChange('orderNumber', e.target.value)} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Ship By Date</div>
            <Input type="datetime-local" value={replacementForm.shipByDate} onChange={(e) => handleReplacementFieldChange('shipByDate', e.target.value)} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Order Status</div>
            <Input value="Awaiting Shipment" disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Customer Username</div>
            <Input value={replacementForm.customerUsername} onChange={(e) => handleReplacementFieldChange('customerUsername', e.target.value)} />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 12, color: '#666' }}>Customer Email</div>
            <Input value={replacementForm.customerEmail} onChange={(e) => handleReplacementFieldChange('customerEmail', e.target.value)} />
          </div>
        </div>

        <Divider>Ship To</Divider>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {ADDRESS_FIELDS.map((fieldKey) => (
            <div key={`shipTo-${fieldKey}`} style={fieldKey === 'residential' || fieldKey === 'addressVerified' ? { gridColumn: '1 / -1' } : {}}>
              {/* Only render the label above for fields that are NOT country or state */}
              {!(fieldKey === 'country' || fieldKey === 'state') && <div style={{ fontSize: 12, color: '#666' }}>{ADDRESS_LABELS[fieldKey] || fieldKey}</div>}
              {renderAddressField('shipTo', fieldKey)}
            </div>
          ))}
        </div>

        <Divider>Items</Divider>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <Text>Add one or multiple items</Text>
          <Button icon={<PlusOutlined />} onClick={handleAddReplacementItem}>
            Add Item
          </Button>
        </div>

        {replacementForm.items.map((item, index) => (
          <div key={`replacement-item-${item.id || index}`} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 8, marginBottom: 8 }}>
            <Input placeholder="SKU" value={item.sku} onChange={(e) => handleReplacementItemChange(index, 'sku', e.target.value)} />
            <Input placeholder="Name" value={item.name} onChange={(e) => handleReplacementItemChange(index, 'name', e.target.value)} />
            <Input
              type="number"
              min={1}
              placeholder="Quantity"
              value={item.quantity}
              onChange={(e) => handleReplacementItemChange(index, 'quantity', e.target.value)}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveReplacementItem(index)} disabled={replacementForm.items.length === 1}>
              Remove
            </Button>
          </div>
        ))}

        <Divider>Notes</Divider>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Internal Notes</div>
          <Input.TextArea
            value={replacementForm.internalNotes}
            onChange={(e) => handleReplacementFieldChange('internalNotes', e.target.value)}
            autoSize={{ minRows: 3 }}
          />
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 12, display: 'flex', gap: 8 }}>
          <Button type="primary" icon={<PlusOutlined />} loading={creatingReplacementOrder} onClick={handleCreateReplacementOrder}>
            Create Replacement Order
          </Button>
          <Button onClick={() => setReplacementDrawerOpen(false)}>Close</Button>
        </div>
      </Drawer>
    </Container>
  );
}
